import React, { useState, useEffect } from "react";
import { Avatar, Tag, message, Popconfirm } from "antd";
import { EditOutlined, CloudUploadOutlined, DeleteOutlined } from '@ant-design/icons';

// Helpers
const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const DocumentCard = ({ doc, onRename, isLocal = false, offline = false, onLocalRename, onSync, onDelete }) => {
  const title = doc?.name || "Personal Journal Entry";
  // Server documents can't be opened while offline; local ones always can.
  const unavailableOffline = !isLocal && offline;
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const [isRenaming, setIsRenaming] = useState(false);

  useEffect(() => {
    setEditedTitle(title);
  }, [title]);

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (isRenaming) return;
    // Validate that name is not empty or just whitespace
    if (!editedTitle || editedTitle.trim() === "") {
      message.error("Document name is required and cannot be empty.");
      setEditedTitle(title); // Revert to original title
      return;
    }
    
    // Validate name length (reasonable limits)
    if (editedTitle.trim().length > 255) {
      message.error("Document name is too long. Maximum 255 characters allowed.");
      return;
    }
    
    // If unchanged, just exit editing mode
    if (editedTitle.trim() === title.trim()) {
      setIsEditing(false);
      return;
    }
    
    // Local presentations rename in localStorage, no API call.
    if (isLocal) {
      onLocalRename?.(doc.id, editedTitle.trim());
      setIsEditing(false);
      return;
    }

    setIsRenaming(true);
    try {
      const payload = {
        attachmentId: doc.attachmentId,
        name: editedTitle.trim(),
        privacyId: doc?.privacyId || 1,
        members: doc?.members || []
      };
      await onRename(payload);
      setIsEditing(false);
    } catch (error) {
      // If rename fails, stay in editing mode and revert to original title
      setEditedTitle(title);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleKeyDown = (e) => {
    if (isRenaming) return; // Prevent actions while renaming
    if (e.key === "Enter") {
      handleSave();
    }
    if (e.key === "Escape") {
      setEditedTitle(title);
      setIsEditing(false);
    }
  };

  const privacyLabel =
    doc?.privacyId === 1 ? "Public" : doc?.privacyId === 2 ? "Private" : null;
  const statusLabel =
    doc?.status === 6 ? "Draft" : doc?.status === 7 ? "Completed" : null;
  const authorName = doc?.creator?.name || "John Doe";
  const authorImg =
    doc?.creator?.image ||
    "https://ui-avatars.com/api/?background=0F766E&color=ffffff&name=" +
    encodeURIComponent(authorName);

  // Dates
  const createdAt = toDate(doc?.createDate);
  const updatedAt = toDate(doc?.updateDate);

  // Description preview
  const getTruncatedDescription = () => {
    if (!doc?.description?.trim()) return "N/A";
    const description = doc.description.trim();
    const maxLength = 100;
    if (description.length > maxLength) {
      return description.substring(0, maxLength) + "...";
    }
    return description;
  };

  const contentPreview = getTruncatedDescription();

  // Word count or size
  const getWordCount = () => {
    const description = doc?.description?.trim();
    if (description && description.length > 0) {
      const words = description.split(/\s+/).filter((word) => word.length > 0);
      return `${words.length} words`;
    }
    return "0 KB";
  };

  const wordCount = getWordCount();
  const createdDate = createdAt ? createdAt.toLocaleDateString() : "N/A";
  const updatedDate = updatedAt ? updatedAt.toLocaleDateString() : "N/A";

  // Map members
  const members =
    doc?.members?.map((memberObj) => ({
      name: memberObj.member.name,
      image:
        memberObj.member.image ||
        `https://ui-avatars.com/api/?background=3B82F6&color=ffffff&name=${encodeURIComponent(
          memberObj.member.name.charAt(0)
        )}`,
    })) || [];

  const handleClick = () => {
    if (isLocal) {
      window.open(`/mileslides/${doc.id}?local=true`, "_blank", "noopener,noreferrer");
      return;
    }
    if (unavailableOffline) {
      message.warning("You're offline. Connect to the internet to open this presentation.");
      return;
    }
    if (!doc?.attachmentId) return;
    const url = doc?.path || `https://pad.workw.com/${doc.attachmentId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className={`document-card ${unavailableOffline ? 'document-card-unavailable' : ''}`}
      style={{ backgroundColor: "var(--card)" }}
      onClick={handleClick}
      title={unavailableOffline ? "Unavailable offline — connect to open this presentation" : undefined}
    >
      {unavailableOffline && (
        <span className="document-card-offline-badge">Offline · unavailable</span>
      )}
      {/* Card Content */}
      <div className="document-card-content">
        {/* Title Section */}
        {isEditing ? (
          <input
            type="text"
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            className="document-card-title-input"
            disabled={isRenaming}
            autoFocus
          />
        ) : (
          <div className="document-card-title-container">
            <h3
              className="document-card-title"
              title={title}
              onDoubleClick={handleDoubleClick}
              onClick={(e) => e.stopPropagation()}
            >
              {title}
            </h3>
            <span className="document-card-icon-wrapper">
              <EditOutlined
                onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
                className="document-card-edit-icon"
              />
            </span>
          </div>
        )}

        {/* Author Section */}
        <div className="document-card-author">
          <img
            src={authorImg}
            alt={authorName}
            className="document-card-author-img"
          />
          <span
            className="document-card-author-name"
            style={{
              fontSize: "12px",
              fontWeight: "400",
            }}
            title={authorName}
          >
            {authorName}
          </span>
        </div>

        {/* Dates and Size */}
        <div className="document-card-dates">
          <div className="document-card-date-row">
            <span>Modified</span>
            <span>{updatedDate}</span>
          </div>

          <div className="document-card-date-row">
            <span>Created</span>
            <span>{createdDate}</span>
          </div>

          <div className="document-card-date-row">
            <span>Size</span>
            <span>{wordCount}</span>
          </div>
        </div>

        {/* Spacer */}
        <div className="document-card-spacer"></div>

        {/* Tags and Members */}
        <div className={`document-card-footer ${isLocal ? 'document-card-footer-local' : ''}`}>
          <div className="document-card-tags">
            {statusLabel && (
              <Tag 
                color="#526bb1" 
                style={{ 
                  borderRadius: "4px", 
                  margin: 0,
                  fontSize: "11px",
                  lineHeight: "18px",
                  padding: "1px 6px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {statusLabel}
              </Tag>
            )}
            {privacyLabel && (
              <Tag 
                color="#526bb1" 
                style={{ 
                  borderRadius: "4px", 
                  margin: 0,
                  fontSize: "11px",
                  lineHeight: "18px",
                  padding: "1px 6px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {privacyLabel}
              </Tag>
            )}
          </div>

          {isLocal ? (
            <div className="document-card-local-actions">
              <button
                type="button"
                className="document-card-action-btn document-card-sync-btn"
                onClick={(e) => { e.stopPropagation(); onSync?.(doc); }}
                title="Sync to server"
              >
                <CloudUploadOutlined />
                <span>Sync</span>
              </button>
              <Popconfirm
                title="Delete this local presentation?"
                okText="Delete"
                okButtonProps={{ danger: true, className: 'export-modal-btn-delete' }}
                cancelButtonProps={{ className: 'export-modal-btn-cancel' }}
                cancelText="Cancel"
                onConfirm={(e) => { e?.stopPropagation?.(); onDelete?.(doc.id); }}
                onCancel={(e) => e?.stopPropagation?.()}
              >
                <button
                  type="button"
                  className="document-card-action-btn document-card-delete-btn"
                  onClick={(e) => e.stopPropagation()}
                  title="Delete local presentation"
                >
                  <DeleteOutlined />
                  <span>Delete</span>
                </button>
              </Popconfirm>
            </div>
          ) : (
            <Avatar.Group
              maxCount={4}
              size="small"
              maxStyle={{
                color: "#ffffff",
                backgroundColor: "#6b7280",
                fontSize: "10px",
              }}
            >
              {members.map((member, index) => (
                <Avatar
                  key={index}
                  src={member.image}
                  alt={member.name}
                  size="small"
                  title={member.name}
                >
                  {!member.image && member.name.charAt(0).toUpperCase()}
                </Avatar>
              ))}
            </Avatar.Group>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentCard;
